import { supabase } from "@/integrations/supabase/client";
import {
  CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  HISTORICAL_V1_CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  assertCanonicalIndividualComplianceRequirementKey,
  type CanonicalIndividualComplianceRequirementKey,
} from "@/data/contractor/compliance/canonicalIndividualCompliance";

export type IndividualPolicyRequirementKey =
  CanonicalIndividualComplianceRequirementKey;

export type IndividualPolicyRequirement = Readonly<{
  requirementKey: IndividualPolicyRequirementKey;
  displayLabel?: string;
  description?: string | null;
  enabled: boolean;
  required: boolean;
  expirationRequired: boolean;
  issueDateRequired: boolean;
  instructions?: string | null;
  sortOrder: number;
}>;

export type IndividualPolicyDraft = Readonly<{
  id: string;
  organizationId: string;
  basePolicyVersionId: string | null;
  revision: number;
  requirements: readonly IndividualPolicyRequirement[];
  versionNote: string | null;
  status: string;
  updatedAt: string;
}>;

export type IndividualPolicyVersion = Readonly<{
  id: string;
  versionNumber: number;
  status: string;
  publishedAt: string | null;
  publishedBy: string | null;
  publishedByLabel: string | null;
  versionNote: string | null;
  requirements: readonly IndividualPolicyRequirement[];
}>;

export type IndividualPolicyImpact = Readonly<{
  draftId: string;
  draftRevision: number;
  currentPolicyVersionId: string | null;
  requirementsAdded: readonly IndividualPolicyRequirementKey[];
  requirementsRemoved: readonly IndividualPolicyRequirementKey[];
  rulesChanged: readonly IndividualPolicyRequirementKey[];
  affectedPersonRecordCount: number;
  currentlyOperationallyVerifiedRecordCount: number;
  recordsThatMayBecomeInProgress: number;
  recordsMissingNewRequirement: number;
  existingEvidenceMayRemainUsable: number;
  existingEvidenceCannotSatisfyNewRules: number;
  previewToken: string;
}>;

export class IndividualPolicyClientError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "IndividualPolicyClientError";
    this.status = status;
    this.code = code;
  }
}

async function invoke(route: string, payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke(route, { body: payload });
  if (error) {
    throw new IndividualPolicyClientError(
      error.message ?? "individual_policy_request_failed",
      Number((error as { context?: { status?: number } }).context?.status ?? 500),
      String((error as { code?: string }).code ?? "individual_policy_request_failed"),
    );
  }
  return (data ?? {}) as Record<string, unknown>;
}

function requirementKey(value: unknown): IndividualPolicyRequirementKey {
  try {
    return assertCanonicalIndividualComplianceRequirementKey(value);
  } catch {
    throw new Error("individual_policy_response_invalid");
  }
}

function requirementKeys(value: unknown): readonly IndividualPolicyRequirementKey[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => requirementKey(item));
}

export function parseIndividualPolicyRequirement(
  row: unknown,
): IndividualPolicyRequirement {
  const value = (row ?? {}) as Record<string, unknown>;
  return {
    requirementKey: requirementKey(value.requirementKey),
    displayLabel: value.displayLabel ? String(value.displayLabel) : undefined,
    description: value.description == null ? null : String(value.description),
    enabled: value.enabled === true,
    required: value.required === true,
    expirationRequired: value.expirationRequired === true,
    issueDateRequired: value.issueDateRequired === true,
    instructions: value.instructions == null ? null : String(value.instructions),
    sortOrder: Number(value.sortOrder ?? 0),
  };
}

function requirements(value: unknown): readonly IndividualPolicyRequirement[] {
  if (!Array.isArray(value)) return [];

  const parsed = value.map((item) => parseIndividualPolicyRequirement(item));
  const keys = new Set(parsed.map((item) => item.requirementKey));
  const orders = new Set(parsed.map((item) => item.sortOrder));
  const isKnownInventory = [
    HISTORICAL_V1_CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
    CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  ].some(
    (inventory) =>
      inventory.length === parsed.length
      && inventory.every((key) => keys.has(key)),
  );

  if (
    keys.size !== parsed.length
    || orders.size !== parsed.length
    || parsed.some((item) => !Number.isInteger(item.sortOrder) || item.sortOrder < 0)
    || !isKnownInventory
  ) {
    throw new Error("individual_policy_response_invalid");
  }

  return parsed.sort((left, right) => left.sortOrder - right.sortOrder);
}

export function serializeIndividualPolicyRequirements(
  value: readonly IndividualPolicyRequirement[],
): readonly IndividualPolicyRequirement[] {
  const parsed = requirements(value);
  const keys = new Set(parsed.map((item) => item.requirementKey));
  const orders = new Set(parsed.map((item) => item.sortOrder));

  if (
    parsed.length !== CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS.length
    || !CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS.every((key) => keys.has(key))
    || orders.size !== CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS.length
    || parsed.some(
      (item) =>
        item.sortOrder < 0
        || item.sortOrder >= CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS.length,
    )
  ) {
    throw new Error("individual_policy_inventory_invalid");
  }

  return parsed.map((item) => ({ ...item }));
}

function draft(row: unknown): IndividualPolicyDraft | null {
  if (!row) return null;
  const value = row as Record<string, unknown>;
  return {
    id: String(value.id),
    organizationId: String(value.organizationId),
    basePolicyVersionId: value.basePolicyVersionId ? String(value.basePolicyVersionId) : null,
    revision: Number(value.revision),
    requirements: requirements(value.requirements),
    versionNote: value.versionNote == null ? null : String(value.versionNote),
    status: String(value.status),
    updatedAt: String(value.updatedAt),
  };
}

export function parseIndividualPolicyVersion(row: unknown): IndividualPolicyVersion {
  const value = (row ?? {}) as Record<string, unknown>;
  return {
    id: String(value.id),
    versionNumber: Number(value.versionNumber),
    status: String(value.status),
    publishedAt: value.publishedAt ? String(value.publishedAt) : null,
    publishedBy: value.publishedBy ? String(value.publishedBy) : null,
    publishedByLabel: value.publishedByLabel ? String(value.publishedByLabel) : null,
    versionNote: value.versionNote == null ? null : String(value.versionNote),
    requirements: requirements(value.requirements),
  };
}

function impact(row: Record<string, unknown>): IndividualPolicyImpact {
  const previewToken = String(row.previewToken ?? "");
  if (!/^[0-9a-f]{64}$/i.test(previewToken)) {
    throw new Error("individual_policy_response_invalid");
  }
  return {
    draftId: String(row.draftId),
    draftRevision: Number(row.draftRevision),
    currentPolicyVersionId: row.currentPolicyVersionId ? String(row.currentPolicyVersionId) : null,
    requirementsAdded: requirementKeys(row.requirementsAdded),
    requirementsRemoved: requirementKeys(row.requirementsRemoved),
    rulesChanged: requirementKeys(row.rulesChanged),
    affectedPersonRecordCount: Number(row.affectedPersonRecordCount),
    currentlyOperationallyVerifiedRecordCount: Number(row.currentlyOperationallyVerifiedRecordCount),
    recordsThatMayBecomeInProgress: Number(row.recordsThatMayBecomeInProgress),
    recordsMissingNewRequirement: Number(row.recordsMissingNewRequirement),
    existingEvidenceMayRemainUsable: Number(row.existingEvidenceMayRemainUsable),
    existingEvidenceCannotSatisfyNewRules: Number(row.existingEvidenceCannotSatisfyNewRules),
    previewToken,
  };
}

export const IndividualCompliancePolicyService = {
  async getWorkspace(organizationId: string) {
    const [activeResponse, draftResponse, historyResponse, catalogResponse] = await Promise.all([
      invoke("individual-compliance-policy-read", { operation: "get_active_policy", organizationId }),
      invoke("individual-compliance-policy-read", { operation: "get_draft_policy", organizationId }),
      invoke("individual-compliance-policy-read", { operation: "get_policy_history", organizationId }),
      invoke("individual-compliance-policy-read", { operation: "list_catalog", organizationId }),
    ]);
    return {
      active: activeResponse.policy ? parseIndividualPolicyVersion(activeResponse.policy) : null,
      draft: draft(draftResponse.draft),
      history: Array.isArray(historyResponse.items)
        ? historyResponse.items.map((item) => parseIndividualPolicyVersion(item))
        : [],
      catalog: Array.isArray(catalogResponse.items)
        ? catalogResponse.items.map((item) => parseIndividualPolicyRequirement(item))
        : [],
    };
  },
  async createDraft(organizationId: string) {
    return draft(
      (await invoke("individual-compliance-policy-manage", {
        operation: "create_draft",
        organizationId,
      })).draft,
    )!;
  },
  async updateDraft(organizationId: string, value: IndividualPolicyDraft) {
    return draft(
      (await invoke("individual-compliance-policy-manage", {
        operation: "update_draft",
        organizationId,
        draftId: value.id,
        expectedRevision: value.revision,
        requirements: serializeIndividualPolicyRequirements(value.requirements),
        versionNote: value.versionNote,
      })).draft,
    )!;
  },
  async discardDraft(organizationId: string, value: IndividualPolicyDraft) {
    await invoke("individual-compliance-policy-manage", {
      operation: "discard_draft",
      organizationId,
      draftId: value.id,
      expectedRevision: value.revision,
    });
  },
  async preview(organizationId: string, value: IndividualPolicyDraft): Promise<IndividualPolicyImpact> {
    serializeIndividualPolicyRequirements(value.requirements);
    return impact(
      await invoke("individual-compliance-policy-read", {
        operation: "preview_publish",
        organizationId,
        draftId: value.id,
        expectedRevision: value.revision,
      }),
    );
  },
  async publish(organizationId: string, value: IndividualPolicyDraft, currentImpact: IndividualPolicyImpact) {
    serializeIndividualPolicyRequirements(value.requirements);
    if (
      !value.versionNote?.trim()
      || currentImpact.draftId !== value.id
      || currentImpact.draftRevision !== value.revision
    ) {
      throw new Error("individual_policy_publish_precondition_failed");
    }
    return invoke("individual-compliance-policy-manage", {
      operation: "publish_policy",
      organizationId,
      draftId: value.id,
      expectedRevision: value.revision,
      previewToken: currentImpact.previewToken,
      idempotencyKey: crypto.randomUUID(),
    });
  },
};
